'use client'

import Image from 'next/image'
import {
  Header,
  HeaderContainer,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenu,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderName,
  HeaderNavigation,
  HeaderSideNavItems,
  OverflowMenu,
  OverflowMenuItem,
  SideNav,
  SideNavItems,
  SkipToContent,
} from '@carbon/react'
import { Application, Certificate, Code, DataBase, Email, FolderOpen, Globe, ServerProxy, Terminal, Contrast, Translate, WatsonHealthAiStatus } from '@carbon/react/icons'
import { isRtl, LOCALES, localeCookie, normalizeLocale, type CommonMessages, type KmerLocale } from '@kmerhosting/i18n'
import { usePathname } from 'next/navigation'
import { useWebsiteTheme } from '@/components/carbon-theme-provider'
import { SiteAccountState } from '@/components/site-account-state'
import type { SiteShellCopy } from '@/lib/site-i18n'

export type HeaderNavItem = {
  slug: string
  label: string
  status: 'available' | 'coming-soon' | 'out-of-stock'
}

type HeaderCopy = SiteShellCopy & Pick<CommonMessages, 'language' | 'services' | 'products' | 'createAccount'>

const serviceMenuGroups = [
  {
    key: 'domainsAndEmail' as const,
    slugs: ['domain-registration', 'email-hosting'],
  },
  {
    key: 'webHosting' as const,
    slugs: [
      'free-hosting',
      'shared-hosting-directadmin',
      'shared-hosting-cpanel',
      'reseller-hosting-directadmin',
      'php-hosting',
      'wordpress-hosting',
      'nodejs-hosting',
      'python-hosting',
      'laravel-hosting',
    ],
  },
  {
    label: 'VPS Hosting',
    slugs: ['lxc-vps-hosting', 'kvm-vps-hosting'],
  },
  {
    key: 'infrastructureProviders' as const,
    slugs: ['bare-metal-server', 'database-hosting', 'ssl-certificates'],
  },
] as const


type MenuVisual = {
  logo?: string
  icon?: typeof Globe
}

const serviceMenuVisuals: Record<string, MenuVisual> = {
  'domain-registration': { icon: Globe },
  'email-hosting': { icon: Email },
  'free-hosting': { icon: Application },
  'shared-hosting-directadmin': { logo: '/platform-logos/directadmin.svg' },
  'shared-hosting-cpanel': { logo: '/platform-logos/cpanel.svg' },
  'reseller-hosting-directadmin': { logo: '/platform-logos/directadmin.svg' },
  'php-hosting': { logo: '/service-logos/php.svg' },
  'wordpress-hosting': { logo: '/service-logos/wordpress.svg' },
  'nodejs-hosting': { logo: '/service-logos/nodejs.svg' },
  'python-hosting': { logo: '/service-logos/python.svg' },
  'laravel-hosting': { logo: '/service-logos/laravel.svg' },
  'lxc-vps-hosting': { logo: '/service-logos/lxc.svg' },
  'kvm-vps-hosting': { icon: ServerProxy },
  'bare-metal-server': { icon: Terminal },
  'database-hosting': { icon: DataBase },
  'ssl-certificates': { icon: Certificate },
}

const productMenuVisuals: Record<string, MenuVisual> = {
  convertsuite: { icon: Code },
  wfilemanager: { icon: FolderOpen },
  webcpl: { icon: Application },
  'kmerhosting-drive': { icon: FolderOpen },
  'kmerhosting-ai-agent': { icon: WatsonHealthAiStatus },
}

function MenuItemVisual({ item, kind }: { item: HeaderNavItem; kind: 'service' | 'product' }) {
  const visual = (kind === 'service' ? serviceMenuVisuals : productMenuVisuals)[item.slug]
  const Icon = visual?.icon ?? Application

  return (
    <span className="site-header__menu-visual" aria-hidden="true">
      {visual?.logo ? <img src={visual.logo} alt="" width="20" height="20" loading="lazy" decoding="async" /> : <Icon size={20} />}
    </span>
  )
}

function MenuItemStatus({ status }: { status: HeaderNavItem['status'] }) {
  if (status === 'available') return null
  return (
    <span className={`site-header__menu-status site-header__menu-status--${status}`}>
      {status === 'coming-soon' ? 'Coming soon' : 'Out of stock'}
    </span>
  )
}

function MenuEntry({ item, kind }: { item: HeaderNavItem; kind: 'service' | 'product' }) {
  return (
    <span className="site-header__menu-entry">
      <MenuItemVisual item={item} kind={kind} />
      <span className="site-header__menu-copy">
        <span className="site-header__menu-label">{item.label}</span>
        <MenuItemStatus status={item.status} />
      </span>
    </span>
  )
}

function stripLocale(pathname: string) {
  const segments = pathname.split('/')
  const first = normalizeLocale(segments[1])
  if (!first) return pathname || '/'
  const rest = segments.slice(2).join('/')
  return rest ? `/${rest}` : '/'
}

function localizedHref(href: string, locale: KmerLocale) {
  if (!href.startsWith('/') || href.startsWith('//')) return href
  if (href === '/docs' || href.startsWith('/docs/')) return href
  const hashIndex = href.indexOf('#')
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : ''
  const path = hashIndex >= 0 ? href.slice(0, hashIndex) : href
  const bare = stripLocale(path || '/')
  if (locale === 'en') return `${bare}${hash}`
  return `/${locale}${bare === '/' ? '/' : bare}${hash}`
}

function ServiceMenuItems({ items, copy, locale, onNavigate }: { items: HeaderNavItem[]; copy: HeaderCopy; locale: KmerLocale; onNavigate?: () => void }) {
  return (
    <>
      {serviceMenuGroups.map((group) => {
        const label = 'label' in group ? group.label : copy[group.key]
        const groupKey = 'key' in group ? group.key : group.label
        return (
          <li className="site-header__service-menu-group" key={groupKey}>
            <span className="site-header__service-menu-group-title">{label}</span>
            <ul className="site-header__service-menu-group-items" aria-label={label}>
              {group.slugs.map((slug) => {
                const item = items.find((candidate) => candidate.slug === slug)
                if (!item) return null
                return (
                  <HeaderMenuItem key={item.slug} href={localizedHref(`/services/${item.slug}`, locale)} onClick={onNavigate}>
                    <MenuEntry item={item} kind="service" />
                  </HeaderMenuItem>
                )
              })}
            </ul>
          </li>
        )
      })}
      <HeaderMenuItem className="site-header__service-menu-all" href={localizedHref('/services', locale)} onClick={onNavigate}>
        {copy.viewAllServices}
      </HeaderMenuItem>
    </>
  )
}

function ProductMenuItems({ items, copy, locale, onNavigate }: { items: HeaderNavItem[]; copy: HeaderCopy; locale: KmerLocale; onNavigate?: () => void }) {
  return (
    <>
      {items.map((item) => (
        <HeaderMenuItem
          className="site-header__product-menu-item"
          key={item.slug}
          href={localizedHref(`/products/${item.slug}`, locale)}
          onClick={onNavigate}
        >
          <MenuEntry item={item} kind="product" />
        </HeaderMenuItem>
      ))}
      <HeaderMenuItem
        className="site-header__product-menu-all"
        href={localizedHref('/roadmap', locale)}
        onClick={onNavigate}
      >
        {copy.productRoadmap}
      </HeaderMenuItem>
    </>
  )
}

function LanguageMenu({ locale, copy, closeSideNav }: { locale: KmerLocale; copy: HeaderCopy; closeSideNav: () => void }) {
  const pathname = usePathname()

  const selectLocale = (next: KmerLocale) => {
    if (next === locale) return
    document.cookie = localeCookie(next, window.location.hostname.endsWith('kmerhosting.com') ? '.kmerhosting.com' : undefined)
    document.documentElement.lang = next
    document.documentElement.dir = isRtl(next) ? 'rtl' : 'ltr'
    const bare = stripLocale(pathname || '/')
    const target = next === 'en' ? bare : `/${next}${bare === '/' ? '/' : bare}`
    window.location.assign(`${target}${window.location.search}${window.location.hash}`)
  }

  const current = LOCALES.find((item) => item.code === locale)
  return (
    <OverflowMenu
      align="bottom-start"
      aria-label={`${copy.language}: ${current?.nativeLabel || locale}`}
      className="site-header__language-menu cds--header__action"
      direction="bottom"
      iconDescription={`${copy.language}: ${current?.nativeLabel || locale}`}
      menuOptionsClass="site-header__language-menu-options"
      onOpen={closeSideNav}
      renderIcon={Translate}
      size="lg"
    >
      {LOCALES.map((item) => (
        <OverflowMenuItem
          key={item.code}
          disabled={item.code === locale}
          itemText={`${item.flag} ${item.nativeLabel}`}
          onClick={() => selectLocale(item.code)}
        />
      ))}
    </OverflowMenu>
  )
}

export function SiteHeader({ serviceItems, productItems, locale, copy }: { serviceItems: HeaderNavItem[]; productItems: HeaderNavItem[]; locale: KmerLocale; copy: HeaderCopy }) {
  const { isDark, toggleTheme } = useWebsiteTheme()
  const pathname = usePathname()
  const barePath = stripLocale(pathname || '/')

  return (
    <HeaderContainer
      render={({ isSideNavExpanded, onClickSideNavExpand }) => {
        const closeSideNav = () => {
          if (isSideNavExpanded) onClickSideNavExpand()
        }

        return (
          <Header aria-label="KmerHosting">
            <SkipToContent href="#main-content" />
            <HeaderMenuButton
              aria-label={isSideNavExpanded ? copy.closeNavigation : copy.openNavigation}
              aria-expanded={isSideNavExpanded}
              isActive={isSideNavExpanded}
              onClick={onClickSideNavExpand}
            />
            <HeaderName href={localizedHref('/', locale)} prefix="" className="site-header__brand">
              <Image
                className="site-header__brand-image"
                src={isDark ? '/brand/logo-white-transparent.png' : '/brand/logo-blue-transparent.png'}
                alt=""
                width={32}
                height={32}
                unoptimized
                loading="eager"
              />
            </HeaderName>

            <HeaderNavigation aria-label={copy.primaryNavigation}>
              <HeaderMenu menuLinkName={copy.services} aria-label={copy.servicesMenu} className="site-header__services-menu">
                <ServiceMenuItems items={serviceItems} copy={copy} locale={locale} />
              </HeaderMenu>
              <HeaderMenu menuLinkName={copy.products} aria-label={copy.productsMenu} className="site-header__products-menu">
                <ProductMenuItems items={productItems} copy={copy} locale={locale} />
              </HeaderMenu>
              <HeaderMenuItem href={localizedHref('/integrations', locale)} isActive={barePath === '/integrations'} aria-current={barePath === '/integrations' ? 'page' : undefined}>{copy.integrations}</HeaderMenuItem>
              <HeaderMenuItem href={localizedHref('/pricing', locale)} isActive={barePath === '/pricing'} aria-current={barePath === '/pricing' ? 'page' : undefined}>{copy.pricing}</HeaderMenuItem>
              <HeaderMenuItem href={localizedHref('/docs', locale)} isActive={barePath === '/docs'} aria-current={barePath === '/docs' ? 'page' : undefined}>{copy.docs}</HeaderMenuItem>
              <HeaderMenuItem href={localizedHref('/help', locale)} isActive={barePath === '/help'} aria-current={barePath === '/help' ? 'page' : undefined}>{copy.help}</HeaderMenuItem>
            </HeaderNavigation>

            <HeaderGlobalBar>
              <LanguageMenu locale={locale} copy={copy} closeSideNav={closeSideNav} />
              <HeaderGlobalAction
                aria-label={isDark ? copy.useLightTheme : copy.useDarkTheme}
                tooltipAlignment="end"
                onClick={toggleTheme}
              >
                <Contrast size={20} />
              </HeaderGlobalAction>
              <SiteAccountState
                locale={locale}
                dashLabel={copy.dash}
                accountLabel={copy.account}
                closeSideNav={closeSideNav}
              />
            </HeaderGlobalBar>

            <SideNav
              aria-label={copy.mobileNavigation}
              expanded={isSideNavExpanded}
              isPersistent={false}
              onOverlayClick={closeSideNav}
            >
              <SideNavItems>
                <HeaderSideNavItems>
                  <HeaderMenu menuLinkName={copy.services} aria-label={copy.servicesMenu} className="site-header__services-menu">
                    <ServiceMenuItems items={serviceItems} copy={copy} locale={locale} onNavigate={closeSideNav} />
                  </HeaderMenu>
                  <HeaderMenu menuLinkName={copy.products} aria-label={copy.productsMenu} className="site-header__products-menu">
                    <ProductMenuItems items={productItems} copy={copy} locale={locale} onNavigate={closeSideNav} />
                  </HeaderMenu>
                  <HeaderMenuItem href={localizedHref('/integrations', locale)} onClick={closeSideNav}>{copy.integrations}</HeaderMenuItem>
                  <HeaderMenuItem href={localizedHref('/pricing', locale)} onClick={closeSideNav}>{copy.pricing}</HeaderMenuItem>
                  <HeaderMenuItem href={localizedHref('/docs', locale)} onClick={closeSideNav}>{copy.docs}</HeaderMenuItem>
                  <HeaderMenuItem href={localizedHref('/help', locale)} onClick={closeSideNav}>{copy.help}</HeaderMenuItem>
                </HeaderSideNavItems>
              </SideNavItems>
            </SideNav>
          </Header>
        )
      }}
    />
  )
}
